import { useCallback, useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
// @mui
import LoadingButton from '@mui/lab/LoadingButton'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import Paper from '@mui/material/Paper'
import Divider from '@mui/material/Divider'
import DialogTitle from '@mui/material/DialogTitle'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import MenuItem from '@mui/material/MenuItem'

// types
// components
import { useSnackbar } from 'src/components/snackbar'
import FormProvider, { RHFTextField } from 'src/components/hook-form'
import axiosInstance from 'src/utils/axios'
import { IUserItem } from 'src/types/user'
import { EditUserSchema, NewUserSchema } from 'src/validators/user.schemas'
import { useBoolean } from 'src/hooks/use-boolean'
import Iconify from 'src/components/iconify'
import { RHFSelect } from 'src/components/hook-form/rhf-select'
import RHFSwitch from 'src/components/hook-form/rhf-switch'
import { useUserStore } from 'src/store/user'

type Props = {
  open: boolean
  onClose: VoidFunction
  onRefleshTable: VoidFunction
  currentUser?: IUserItem
}

export default function UserCreateEditForm({ onRefleshTable, currentUser, open, onClose }: Props) {
  const { enqueueSnackbar } = useSnackbar()
  const { user } = useUserStore()

  const usersPermissons = useMemo(() => {
    if (user?.role === 'admin') {
      return [
        { label: 'Usuário/Vendedor', value: 'user' },
        { label: 'Franqueado', value: 'franchisee' },
        { label: 'Administrador', value: 'admin' },
      ]
    }
    return [{ label: 'Usuário/Vendedor', value: 'user' }]
  }, [user])

  const password = useBoolean()
  const passwordConfirm = useBoolean()

  const defaultValues = useMemo(
    () => ({
      ...currentUser,
      name: currentUser?.name || '',
      active: currentUser?.active,
      email: currentUser?.email || '',
      comission: currentUser?.comission || 0,
      document: currentUser?.document || '',
      role: currentUser?.role || 'user',
      photo: currentUser?.photo || null,
      // Inicializa explicitamente para que o estado dos switches sempre
      // reflita o que será salvo. Sem isso, na criação os campos ficam
      // `undefined` e o `.default(true)` do schema marca tudo como ativo.
      allowedPayments: {
        money: currentUser?.allowedPayments?.money ?? true,
        pix: currentUser?.allowedPayments?.pix ?? true,
        credit: currentUser?.allowedPayments?.credit ?? true,
        debit: currentUser?.allowedPayments?.debit ?? true,
        cashless: currentUser?.allowedPayments?.cashless ?? true,
      },
      password: '',
      password_confirmation: '',
    }),
    [currentUser],
  )

  const methods = useForm<IUserItem>({
    resolver: yupResolver(currentUser ? EditUserSchema : NewUserSchema),
    defaultValues,
  })

  const {
    control,
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods

  // O componente fica sempre montado (apenas alterna `open`), então o
  // `useForm` lê o `defaultValues` só uma vez. Ao abrir o modal resetamos
  // com os valores corretos para evitar estado antigo e garantir que os
  // switches de pagamento reflitam exatamente o que será salvo.
  useEffect(() => {
    if (open) {
      reset(defaultValues)
    }
  }, [open, defaultValues, reset])

  const onSubmit = useCallback(
    async (data: IUserItem) => {
      try {
        let userId = currentUser?._id

        // Normaliza para booleanos explícitos. Sem isso, campos `undefined`
        // são marcados como `true` pelo `.default(true)` do schema (front e back).
        const allowedPayments = {
          money: !!data.allowedPayments?.money,
          pix: !!data.allowedPayments?.pix,
          credit: !!data.allowedPayments?.credit,
          debit: !!data.allowedPayments?.debit,
          cashless: !!data.allowedPayments?.cashless,
        }

        if (currentUser) {
          await axiosInstance.put(`/user/${currentUser._id}`, {
            ...data,
            allowedPayments,
            photo: undefined,
            email: undefined,
            password: undefined,
          })
        } else {
          const { data: response } = await axiosInstance.post('/user', {
            ...data,
            allowedPayments,
          })

          userId = response._id
        }

        if (typeof data.photo !== 'string' && data.photo) {
          const formDataUpload: FormData = new FormData()
          formDataUpload.append('photo', data.photo)
          try {
            await axiosInstance.post(`/user/${userId}/photo`, formDataUpload)
            enqueueSnackbar('Foto de perfil atualizada com sucesso !')
          } catch (error) {
            enqueueSnackbar('Erro ao enviar a foto perfil !', {
              variant: 'error',
            })
          }
        }

        // Mostra a mensagem de sucesso
        enqueueSnackbar(
          currentUser ? 'Alteração realizada com sucesso!' : 'Usuario criado com sucesso!',
          { variant: 'success' },
        )

        // Fecha o modal primeiro
        onClose()

        // Aguarda a animação do modal fechar antes de resetar e atualizar
        setTimeout(() => {
          reset()
          onRefleshTable()
        }, 300)
      } catch (error) {
        if (error?.message) {
          enqueueSnackbar(error?.message, {
            variant: 'error',
          })
        } else {
          enqueueSnackbar('Erro na requisição!', {
            variant: 'error',
          })
        }
      }
    },
    [enqueueSnackbar, onClose, reset, currentUser, onRefleshTable],
  )

  return (
    <Dialog
      fullWidth
      maxWidth='md'
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          maxWidth: { xs: '100%', sm: 600, md: 800 },
          m: { xs: 2, sm: 4 },
        },
      }}
    >
      <FormProvider methods={methods}>
        <DialogTitle sx={{ pb: 2 }}>
          <Stack direction='row' alignItems='center' spacing={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: 'primary.lighter',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Iconify
                icon={currentUser ? 'solar:user-bold' : 'solar:user-plus-bold'}
                width={24}
                sx={{ color: 'primary.main' }}
              />
            </Box>
            <Box>
              <Typography variant='h5' fontWeight={600}>
                {currentUser ? 'Editar Usuário' : 'Novo Usuário'}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {currentUser
                  ? 'Atualize as informações do vendedor'
                  : 'Cadastre um novo vendedor no sistema'}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={3}>
            {/* Seção: Informações Básicas */}
            <Paper
              sx={{
                p: { xs: 2, sm: 3 },
                border: '1px solid',
                borderColor: 'grey.200',
              }}
            >
              <Stack spacing={2.5}>
                <Stack direction='row' alignItems='center' spacing={1}>
                  <Iconify icon='solar:user-id-bold' width={20} sx={{ color: 'text.secondary' }} />
                  <Typography variant='subtitle1' fontWeight={600}>
                    Informações Básicas
                  </Typography>
                </Stack>

                <Stack spacing={2.5}>
                  <RHFTextField
                    name='name'
                    label='Nome do Vendedor'
                    placeholder='Ex: João, Maria, Vendedor 1... (não precisa ser o nome real)'
                    fullWidth
                    helperText='Pode ser um apelido ou identificador. Este nome aparecerá nos relatórios.'
                  />

                  <RHFTextField
                    name='email'
                    label='Email para Login'
                    placeholder='vendedor1@loja.com (não precisa ser válido)'
                    fullWidth
                    helperText='Serve apenas para fazer login no aplicativo da máquina. Pode ser qualquer texto no formato de email.'
                  />

                  <Box
                    display='grid'
                    gap={2}
                    gridTemplateColumns={{
                      xs: '1fr',
                      sm: 'repeat(2, 1fr)',
                    }}
                  >
                    <RHFTextField
                      name='document'
                      mask='cpf'
                      label='CPF (Opcional)'
                      placeholder='Deixe em branco se não tiver'
                      fullWidth
                      helperText='Campo opcional para controle interno'
                    />
                    <RHFSelect
                      fullWidth
                      name='role'
                      label='Tipo de Conta'
                      InputLabelProps={{ shrink: true }}
                      PaperPropsSx={{ textTransform: 'capitalize' }}
                    >
                      {usersPermissons.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </RHFSelect>
                  </Box>
                </Stack>
              </Stack>
            </Paper>

            {/* Seção: Formas de Pagamento */}
            <Paper
              sx={{
                p: { xs: 2, sm: 3 },
                border: '1px solid',
                borderColor: 'grey.200',
              }}
            >
              <Stack spacing={2.5}>
                <Stack direction='row' alignItems='center' spacing={1}>
                  <Iconify icon='solar:card-bold' width={20} sx={{ color: 'text.secondary' }} />
                  <Typography variant='subtitle1' fontWeight={600}>
                    Formas de Pagamento Permitidas
                  </Typography>
                </Stack>

                <Typography variant='body2' color='text.secondary'>
                  Selecione quais métodos de pagamento este vendedor pode aceitar
                </Typography>

                <Box
                  display='grid'
                  gap={2}
                  gridTemplateColumns={{
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                  }}
                >
                  <Paper
                    sx={{
                      p: 1.5,
                      border: '1px solid',
                      borderColor: 'grey.300',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <RHFSwitch
                      name='allowedPayments.money'
                      label={
                        <Stack direction='row' alignItems='center' spacing={1}>
                          <Iconify
                            icon='solar:money-bold'
                            width={18}
                            sx={{ color: 'success.main' }}
                          />
                          <Typography variant='body2' fontWeight={500}>
                            Dinheiro
                          </Typography>
                        </Stack>
                      }
                    />
                  </Paper>

                  <Paper
                    sx={{
                      p: 1.5,
                      border: '1px solid',
                      borderColor: 'grey.300',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <RHFSwitch
                      name='allowedPayments.pix'
                      label={
                        <Stack direction='row' alignItems='center' spacing={1}>
                          <Iconify
                            icon='solar:qr-code-bold'
                            width={18}
                            sx={{ color: 'warning.main' }}
                          />
                          <Typography variant='body2' fontWeight={500}>
                            PIX
                          </Typography>
                        </Stack>
                      }
                    />
                  </Paper>

                  <Paper
                    sx={{
                      p: 1.5,
                      border: '1px solid',
                      borderColor: 'grey.300',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <RHFSwitch
                      name='allowedPayments.credit'
                      label={
                        <Stack direction='row' alignItems='center' spacing={1}>
                          <Iconify icon='solar:card-bold' width={18} sx={{ color: 'error.main' }} />
                          <Typography variant='body2' fontWeight={500}>
                            Crédito
                          </Typography>
                        </Stack>
                      }
                    />
                  </Paper>

                  <Paper
                    sx={{
                      p: 1.5,
                      border: '1px solid',
                      borderColor: 'grey.300',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <RHFSwitch
                      name='allowedPayments.debit'
                      label={
                        <Stack direction='row' alignItems='center' spacing={1}>
                          <Iconify
                            icon='solar:card-2-bold'
                            width={18}
                            sx={{ color: 'info.main' }}
                          />
                          <Typography variant='body2' fontWeight={500}>
                            Débito
                          </Typography>
                        </Stack>
                      }
                    />
                  </Paper>

                  <Paper
                    sx={{
                      p: 1.5,
                      border: '1px solid',
                      borderColor: 'grey.300',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <RHFSwitch
                      name='allowedPayments.cashless'
                      label={
                        <Stack direction='row' alignItems='center' spacing={1}>
                          <Iconify
                            icon='solar:card-send-bold'
                            width={18}
                            sx={{ color: 'secondary.main' }}
                          />
                          <Typography variant='body2' fontWeight={500}>
                            CashLess
                          </Typography>
                        </Stack>
                      }
                    />
                  </Paper>
                </Box>
              </Stack>
            </Paper>

            {/* Seção: Senha (apenas para novo usuário) */}
            {!currentUser && (
              <Paper
                sx={{
                  p: { xs: 2, sm: 3 },
                  border: '1px solid',
                  borderColor: 'info.light',
                  bgcolor: 'info.lighter',
                }}
              >
                <Stack spacing={2.5}>
                  <Stack spacing={1}>
                    <Stack direction='row' alignItems='center' spacing={1}>
                      <Iconify
                        icon='solar:lock-password-bold'
                        width={20}
                        sx={{ color: 'info.main' }}
                      />
                      <Typography variant='subtitle1' fontWeight={600} color='info.dark'>
                        Senha para o Aplicativo
                      </Typography>
                    </Stack>
                    <Paper
                      sx={{
                        p: 1.5,
                        bgcolor: 'white',
                        border: '1px solid',
                        borderColor: 'info.light',
                      }}
                    >
                      <Stack direction='row' spacing={1}>
                        <Iconify
                          icon='solar:info-circle-bold'
                          width={18}
                          sx={{ color: 'info.main', flexShrink: 0, mt: 0.2 }}
                        />
                        <Typography variant='body2' color='text.secondary'>
                          <strong>Pode ser simples!</strong> Esta senha é usada apenas para login no
                          aplicativo da máquina. Exemplos: 1234, 0000, abc123
                        </Typography>
                      </Stack>
                    </Paper>
                  </Stack>

                  <Box
                    display='grid'
                    gap={2}
                    gridTemplateColumns={{
                      xs: '1fr',
                      sm: 'repeat(2, 1fr)',
                    }}
                  >
                    <RHFTextField
                      name='password'
                      label='Senha'
                      placeholder='Ex: 1234'
                      autoComplete='new-password'
                      type={password.value ? 'text' : 'password'}
                      helperText='Mínimo 4 caracteres'
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position='end'>
                            <IconButton onClick={password.onToggle} edge='end'>
                              <Iconify
                                icon={password.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'}
                              />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <RHFTextField
                      name='password_confirmation'
                      label='Confirmar Senha'
                      placeholder='Digite novamente'
                      autoComplete='new-password'
                      type={passwordConfirm.value ? 'text' : 'password'}
                      helperText='Repita a mesma senha'
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position='end'>
                            <IconButton onClick={passwordConfirm.onToggle} edge='end'>
                              <Iconify
                                icon={
                                  passwordConfirm.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'
                                }
                              />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Stack>
              </Paper>
            )}

            {/* Seção: Status (apenas para edição) */}
            {currentUser && (
              <Paper
                sx={{
                  p: { xs: 2, sm: 3 },
                  border: '1px solid',
                  borderColor: 'grey.200',
                }}
              >
                <FormControlLabel
                  labelPlacement='start'
                  control={
                    <Controller
                      name='active'
                      control={control}
                      render={({ field }) => (
                        <Switch
                          {...field}
                          checked={field.value}
                          onChange={(event) => field.onChange(event.target.checked)}
                          color='success'
                        />
                      )}
                    />
                  }
                  label={
                    <Stack spacing={0.5}>
                      <Stack direction='row' alignItems='center' spacing={1}>
                        <Iconify
                          icon='solar:power-bold'
                          width={20}
                          sx={{ color: 'text.secondary' }}
                        />
                        <Typography variant='subtitle1' fontWeight={600}>
                          Status da Conta
                        </Typography>
                      </Stack>
                      <Typography variant='body2' color='text.secondary'>
                        Ative ou desative o acesso deste usuário ao sistema
                      </Typography>
                    </Stack>
                  }
                  sx={{ mx: 0, width: 1, justifyContent: 'space-between' }}
                />
              </Paper>
            )}
          </Stack>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: 1 }}>
          <Button
            variant='outlined'
            onClick={onClose}
            size='large'
            sx={{ minWidth: { xs: '100%', sm: 120 } }}
          >
            Cancelar
          </Button>

          <LoadingButton
            type='button'
            onClick={handleSubmit(onSubmit)}
            variant='contained'
            loading={isSubmitting}
            size='large'
            startIcon={<Iconify icon='solar:check-circle-bold' />}
            sx={{ minWidth: { xs: '100%', sm: 140 }, fontWeight: 600 }}
          >
            {currentUser ? 'Salvar Alterações' : 'Cadastrar Usuário'}
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  )
}
